import cn from 'classnames';
import { ComponentPropsWithoutRef, CSSProperties, FC, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { animated, useTransition } from 'react-spring';
import { useDom } from '../../contexts/dom';
import styles from './modal.module.css';

interface PropTypes extends ComponentPropsWithoutRef<'div'> {
  isOpen: boolean;
  onRequestClose: () => void;
}

const Modal: FC<PropTypes> = ({ isOpen, children, className, style, onRequestClose, ...rest }) => {
  const { modalWindowMountPointRef } = useDom();

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [isOpen]);

  const modalTransitions = useTransition<boolean, CSSProperties>(isOpen, null, {
    from: {
      ...style,
      opacity: 0,
      transform: 'translateY(30px)',
    },
    enter: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    leave: { opacity: 0, transform: 'translateY(-100px)' },
  });

  const modalBackdropTransitions = useTransition<boolean, CSSProperties>(isOpen, null, {
    from: {
      opacity: 0,
    },
    enter: {
      opacity: 1,
    },
    leave: {
      opacity: 0,
    },
  });

  if (!modalWindowMountPointRef.current) {
    return null;
  }

  const modal = (
    <>
      {modalTransitions.map(
        ({ item, props, key }) =>
          item && (
            <div
              key={key}
              className="z-modal fixed inset-0 flex items-center justify-center overflow-auto lg:py-10"
              data-test="modal"
              {...rest}
              onClick={onRequestClose}
            >
              <animated.div
                onClick={(e) => e.stopPropagation()}
                key={key}
                style={props}
                className={cn(
                  className,
                  'bg-other-white my-auto overflow-hidden rounded-sm shadow-lg'
                )}
              >
                {children}
              </animated.div>
            </div>
          )
      )}

      {modalBackdropTransitions.map(
        ({ item, props, key }) =>
          item && (
            <div key={key} className="z-backdrop relative">
              <animated.div key={key} style={props}>
                <div data-test="modal-backdrop" className={cn(styles.backdrop, styles.noBlur)} />
              </animated.div>
            </div>
          )
      )}
    </>
  );

  return ReactDOM.createPortal(modal, modalWindowMountPointRef.current);
};

export default Modal;
